import React, { useEffect } from 'react'
import io from 'socket.io-client'
import useSWR from 'swr'
import { Spin } from 'antd'
import { EIncomeLevel, ESocketEvent } from '../../../typings'
import { IncomesLevel } from '../../../components'
import { buildCacheKey, snakeCaseToTitle } from '../../../util'
import { useParamsIncomesQuerySync } from '../../../hooks'
import constants from '../../../constants'
import api from '../../../api'

interface Props {
	level: Entities.Income.Level
}

export interface IRouterParams
	extends Omit<Recoil.ExploreIncomesMatch, 'year' | 'quarter'> {
	year?: string
	quarter?: string
}

const ExploreIncomesPage: React.FC<Props> = ({ level }) => {
	const query = useParamsIncomesQuerySync()
	const aggregationCacheKey = buildCacheKey('income', 'aggregation', {
		extraDependencies: [level],
		query,
	})

	const { data: aggregationData, mutate } = useSWR<
		Charts.TreemapData | undefined
	>(aggregationCacheKey, async () => {
		try {
			const params = { level, match: query.match }
			const response = await api.get('/incomes/aggregate', { params })
			const processedData: Charts.TreemapData = {
				title: response.data?.length
					? 'Explorar Gastos'
					: 'No se encontraron ingresos',
				size: 0,
				children: response.data.map((entry: any, i: number) => ({
					...entry,
					title: `${level === EIncomeLevel.Quarter ? 'Q' : ''}${
						level === EIncomeLevel.Institution
							? entry.abbreviation
							: snakeCaseToTitle(`${entry.title}`) || ''
					} ($${entry.size})`,
					realTitle: entry.title, // this has to be done because react-vis has its own prop "title" which is the that is displayed, but we still need access to the real aggregation title to perfom the WS-triggered update
					color: constants.moduloToColorMap[i % 16],
				})),
				style: {
					display: 'flex',
					border: '1px solid #f7f7f7',
					cursor: 'pointer',
				},
				color: '#f7f7f7',
			}

			return processedData
		} catch (error) {
			console.error(error)
		}
	})

	useEffect(() => {
		const socket = io(process.env.REACT_APP_API_BASE_URL || 'error')
		const event = `${ESocketEvent.NewIncome}${level}${query.match}`
		socket.on(event, (income: Entities.Income.Object) => {
			mutate((aggregationData) => {
				if (aggregationData) {
					if (
						level !== EIncomeLevel.Federal &&
						level !== EIncomeLevel.Quarter
					) {
						const indexOfEntryToUpdate = aggregationData.children.findIndex(
							({ realTitle }) => realTitle === income[level]?.name
						)
						// TODO: create util functions for these kinds of array manipulations 👇
						const newChildren = [
							...aggregationData.children.slice(0, indexOfEntryToUpdate),
							{
								...aggregationData.children[indexOfEntryToUpdate],
								size:
									aggregationData.children[indexOfEntryToUpdate].size +
									income.amount,
							},
							...aggregationData.children.slice(indexOfEntryToUpdate + 1),
						]

						const newData: Charts.TreemapData = {
							...aggregationData,
							size: aggregationData.size + income.amount,
							children: newChildren.map(({ title, size }) => ({
								title,
								size,
								style: { cursor: 'pointer' },
								children: [],
							})),
						}

						return newData
					}
				}
			})
		})

		return () => {
			socket.off(event)
			socket.disconnect()
		}
	}, [aggregationCacheKey, query, level, mutate])

	return (
		<div>
			<Spin spinning={!aggregationData}>
				{aggregationData && (
					<IncomesLevel data={aggregationData} level={level} />
				)}
			</Spin>
		</div>
	)
}

export default ExploreIncomesPage
